import { Badge } from "../components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "../components/base/tooltip/tooltip";
import { REASONS, type ReasonCode, type ReasonTone } from "../lib/reasons";
import { cx } from "../utils/cx";

const toneToColor: Record<ReasonTone, "gray" | "warning" | "error" | "brand" | "success"> = {
    gray: "gray",
    warning: "warning",
    error: "error",
    brand: "brand",
    success: "success",
};

export interface ReasonBadgeProps {
    code: ReasonCode;
    size?: "sm" | "md" | "lg";
    /** Show «why» and «what next» in a tooltip (default true). */
    explain?: boolean;
    className?: string;
}

/** Persian label for a reason code, with the reason and the next step on hover/focus (spec §14.6.3). */
export const ReasonBadge = ({ code, size = "sm", explain = true, className }: ReasonBadgeProps) => {
    const reason = REASONS[code];
    const badge = (
        <Badge type="pill-color" color={toneToColor[reason.tone]} size={size} className={className}>
            {reason.label}
        </Badge>
    );

    if (!explain) return badge;

    return (
        <Tooltip title={reason.why} description={reason.next} placement="top">
            <TooltipTrigger aria-label={`${reason.label}: ${reason.why}`} className="cursor-help">
                {badge}
            </TooltipTrigger>
        </Tooltip>
    );
};

/** Inline block version: reason plus the next step, for detail panels and timelines. */
export const ReasonNote = ({ code, className }: { code: ReasonCode; className?: string }) => {
    const reason = REASONS[code];
    return (
        <div className={cx("flex flex-col gap-1 text-sm", className)}>
            <p className="text-secondary">{reason.why}</p>
            <p className="text-tertiary">{reason.next}</p>
        </div>
    );
};
